<template>
  <template v-if="props.position === 'inline'">
    <div class="font-poppins flex flex-row items-center modern-color-theme" :class="computedClasses" role="status" aria-live="polite" data-component-name="VSValidation">
      <VIcon name="Solid/exclamation-triangle" class="mr-1" />
      <p class="text-sm leading-5 font-normal" v-text="props.message" />
    </div>
  </template>
  <VTeleport v-else-if="isFloating" to="body">
    <div
      ref="floatingEl"
      class="font-poppins flex flex-row items-center modern-color-theme fixed z-[1003] py-1 px-2 shadow-lg rounded-md text-sm border"
      :class="computedClasses"
      :style="positionData.style"
      role="status"
      aria-live="polite"
      data-component-name="VSValidation"
    >
      <VIcon name="Solid/exclamation-triangle" class="mr-1" />
      <p class="text-sm leading-5 font-normal" v-text="props.message" />
    </div>
  </VTeleport>
</template>
<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import VTeleport from '@component-library/utilities/VTeleport.vue';
import VIcon from "@component-library/labels/VIcon.vue";
import { getDOMRect, useAnimationFramePosition } from '@component-utils/position';
import { useWindowSize } from '@component-utils/window';

defineOptions({
  name: 'VSValidation'
})

type Type = 'warning' | 'error'
type Position = 'inline' | 'floating'

const props = withDefaults(
  defineProps<{
    type: Type,
    message: string,
    position?: Position,
    anchor?: HTMLElement | null,
    anchorFocused?: boolean
  }>(),
  {
    position: 'inline',
    anchor: undefined,
    anchorFocused: true
  }
)

const computedClasses = computed(() => ({
  'text-yellow-500': props.type === 'warning',
  'border-yellow-500 bg-yellow-50': props.position === 'floating' && props.type === 'warning',
  'text-red-550': props.type === 'error',
  'border-red-550 bg-red-50': props.position === 'floating' && props.type === 'error',
}))

const floatingEl = useTemplateRef('floatingEl')

const OFFSET = 4

const windowSize = useWindowSize()

const isFloating = computed(() => props.position === 'floating' && !!props.anchor && props.anchorFocused)

const positionData = useAnimationFramePosition(
  isFloating,
  () => {
    if (!floatingEl.value) return {}

    const anchorRect = getDOMRect(props.anchor)
    const { bottom: windowHeight } = windowSize.value

    // Check if anchor is visible in viewport
    const anchorTop = anchorRect.top
    const anchorBottom = anchorRect.bottom

    // Calculate available space above and below
    const spaceBelow = windowHeight - anchorBottom
    const spaceAbove = anchorTop

    // Get actual height if available
    const height = floatingEl.value.offsetHeight

    // Determine position based on available space
    let top: number
    let position: 'top' | 'bottom'

    if (spaceBelow >= height + OFFSET) {
      // Enough space below - position below anchor
      top = anchorBottom + OFFSET
      position = 'bottom'
    } else if (spaceAbove >= height + OFFSET) {
      // Not enough space below but enough above - position above anchor
      top = anchorTop - height - OFFSET
      position = 'top'
    } else {
      // Not enough space on either side - position below if anchor is in upper half
      if (anchorTop < windowHeight / 2) {
        top = anchorBottom + OFFSET
        position = 'bottom'
      } else {
        top = anchorTop - height - OFFSET
        position = 'top'
      }
    }

    return {
      position,
      style: {
        left: `${anchorRect.left}px`,
        top: `${top}px`
      }
    }
  }
)
</script>